import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import { presetToISORange } from 'shared/utils/timePresets';
import FailureFilter from 'features/agent-analytics/components/failures/FailureFilter';
import Sparkline from 'features/issues/components/Sparkline';
import { formatDate, formatRelativeTime } from 'features/agent-analytics/components/runCells';
import FailureTags from 'features/agent-analytics/components/failures/FailureTags';
import FailureDrawer from 'features/agent-analytics/components/failures/FailureDrawer';
import FailuresSummary from 'features/agent-analytics/components/failures/FailuresSummary';
import { listFailures } from 'features/agent-analytics/components/failures/failuresApi';
import {
  activitySearchWrapperClass,
  activitySearchInputClass,
  tableHeaderCellClass,
  tableDataCellClass,
  tableRowClass,
  tableHeaderRowClass,
} from 'features/activity/styles/dataTableStyles';
import TableFooter from 'shared/components/tables/TableFooter';

const PAGE_SIZE = 50;
const DEFAULT_PERIOD = '7d';

function SearchIcon() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

const muted = <span className="text-[color:var(--text-muted)]">—</span>;

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** A header that sorts its column; the active one shows its direction. */
function SortTh({ id, sort, onSort, children }) {
  const active = sort.key === id;
  const Arrow = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={tableHeaderCellClass} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(id)}
        className={`inline-flex items-center gap-1 uppercase ${active ? 'text-[color:var(--text-primary)]' : ''} hover:text-[color:var(--text-primary)]`}
      >
        {children}
        {active ? <Arrow size={11} /> : null}
      </button>
    </th>
  );
}

/**
 * Failures tab — every hard failure in the period, grouped deterministically
 * by agent · tool · error type · class. Filtered, sorted and paged on the
 * server. A row opens a drawer with every occurrence; an occurrence opens its
 * trace.
 */
export default function FailuresView({ teamId }) {
  // Same picker as Traces, but a week by default: less to load.
  const [period, setPeriod] = useState(() => ({ preset: DEFAULT_PERIOD, ...presetToISORange(DEFAULT_PERIOD) }));
  const onTimeFilterChange = (next) => {
    const preset = next?.preset || (next?.type === 'custom' ? 'custom' : DEFAULT_PERIOD);
    const range = next?.from || next?.to ? { from: next.from || '', to: next.to || '' } : presetToISORange(preset);
    setPeriod({ preset, ...(range || {}) });
  };
  const [sel, setSel] = useState({ agents: null, tools: null, categories: null });
  const [labels, setLabels] = useState({});
  const { agents: agentSel, tools: toolSel, categories: categorySel } = sel;
  const [search, setSearch] = useState('');
  const q = useDebounced(search, 250);
  const [sort, setSort] = useState({ key: 'last_seen', dir: 'desc' });
  const onSort = (key) => setSort((prev) => (
    prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
  ));

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const requestIdRef = useRef(0);

  const periodKey = JSON.stringify(period);
  const filters = { agents: agentSel, tools: toolSel, categories: categorySel };
  const filtersKey = JSON.stringify(filters);

  const load = useCallback(async (offset = 0) => {
    if (!teamId) return;
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const d = await listFailures(teamId, {
        period: JSON.parse(periodKey),
        filters: { ...JSON.parse(filtersKey), search: q },
        sort: sort.key,
        dir: sort.dir,
        offset,
        limit: PAGE_SIZE,
      });
      if (id !== requestIdRef.current) return;
      setRows((prev) => (offset === 0 ? d.clusters : [...prev, ...d.clusters]));
      setMeta(d);
    } catch (e) {
      if (id !== requestIdRef.current) return;
      if (offset === 0) { setRows([]); setMeta(null); }
      setError(e?.response?.data?.error || e?.message || 'Failed to load failures');
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, [teamId, periodKey, filtersKey, q, sort]);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => { setSelectedIdx(-1); }, [periodKey, filtersKey, q, sort]);

  const total = meta?.total || 0;
  const filtered = Boolean(q.trim() || agentSel || toolSel || categorySel);
  const selected = selectedIdx >= 0 ? rows[selectedIdx] : null;

  return (
    <>
      <div className="flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <EventsTimeFilter
            selectedPreset={period.preset}
            onTimeFilterChange={onTimeFilterChange}
          />

          <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px]">
            <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
              {loading && !meta ? '…' : total.toLocaleString()}
            </span>
            <span className="ml-1.5 text-[color:var(--text-muted)]">failure{total === 1 ? '' : 's'}</span>
          </div>

          <div className={`${activitySearchWrapperClass} min-w-[220px] flex-1 sm:max-w-sm`}>
            <SearchIcon />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search message, span, tool, error type…"
              className={activitySearchInputClass}
            />
          </div>

          <FailureFilter
            teamId={teamId}
            period={period}
            value={sel}
            labels={labels}
            onChange={(next, nextLabels) => { setSel(next); setLabels(nextLabels); }}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-8 pt-3">
        {error ? (
          <div className="rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : null}

        {meta?.clamped ? (
          <div className="text-[12px] text-[color:var(--text-muted)]">
            Showing the last 90 days of this period.
          </div>
        ) : null}

        {meta?.truncated ? (
          <div className="text-[12px] text-[color:var(--text-muted)]">
            Showing the most frequent failures only. Narrow the period to see the rest.
          </div>
        ) : null}

        <FailuresSummary
          teamId={teamId}
          period={period}
          filters={filters}
          search={search}
        />

        {loading && !meta ? (
          <PageLoader label="Loading failures" />
        ) : rows.length === 0 ? (
          <div className="flex h-48 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-muted)]">
            {filtered ? 'No failures match.' : 'No failures in this period.'}
          </div>
        ) : (
          <div className="shrink-0 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] table-fixed text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
                <colgroup>
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th className={tableHeaderCellClass}>Error</th>
                    <th className={tableHeaderCellClass}>Category</th>
                    <th className={tableHeaderCellClass}>Agent</th>
                    <th className={tableHeaderCellClass}>Tool</th>
                    <SortTh id="count" sort={sort} onSort={onSort}>Count</SortTh>
                    <SortTh id="users" sort={sort} onSort={onSort}>Users</SortTh>
                    <th className={tableHeaderCellClass}>Trend</th>
                    <SortTh id="last_seen" sort={sort} onSort={onSort}>Last seen</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c, idx) => (
                    <tr
                      key={c.key}
                      className={`${tableRowClass} ${selected?.key === c.key ? 'bg-surface-hover' : ''}`}
                      onClick={() => setSelectedIdx(idx)}
                    >
                      <td className={tableDataCellClass}>
                        <span className="block truncate text-[12px] font-medium text-[color:var(--text-primary)]" title={c.message || ''}>
                          {c.message || muted}
                        </span>
                      </td>
                      <td className={tableDataCellClass}>
                        <FailureTags cluster={c} wrap={false} />
                      </td>
                      <td className={tableDataCellClass}>
                        <span className="block truncate text-[12px] text-[color:var(--text-primary)]">{c.agent_name || muted}</span>
                      </td>
                      <td className={tableDataCellClass}>
                        {c.kind === 'run'
                          ? <span className="text-[12px] text-[color:var(--text-muted)]">Run</span>
                          : <span className="block truncate font-mono text-[12px] text-[color:var(--text-primary)]" title={c.tool || ''}>{c.tool || muted}</span>}
                      </td>
                      <td className={tableDataCellClass}>
                        <span className="tabular-nums text-[12px] font-semibold text-[color:var(--text-primary)]">{c.count.toLocaleString()}</span>
                      </td>
                      <td className={tableDataCellClass}>
                        <span className="tabular-nums text-[12px] text-[color:var(--text-primary)]">
                          {c.users ? `${c.users.toLocaleString()}${c.users_is_lower_bound ? '+' : ''}` : muted}
                        </span>
                      </td>
                      <td className={tableDataCellClass}>
                        <Sparkline
                          counts={(meta?.days || []).map((d, i) => ({ bucket: d, count: c.daily[i] || 0 }))}
                          width={72}
                          height={18}
                          compact
                        />
                      </td>
                      <td className={tableDataCellClass}>
                        <span className="tabular-nums text-[11px] text-[color:var(--text-muted)]" title={formatDate(c.last_seen)}>
                          {formatRelativeTime(c.last_seen)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer: count on the left, Load more centred — as on Traces. */}
        <TableFooter
          shown={rows.length}
          total={meta?.clustersTotal || rows.length}
          hasMore={Boolean(meta?.hasMore)}
          loading={loading}
          onLoadMore={() => load(rows.length)}
        />
      </div>

      {selected ? (
        <FailureDrawer
          teamId={teamId}
          cluster={selected}
          period={period}
          search={q}
          days={meta?.days}
          onClose={() => setSelectedIdx(-1)}
          onPrev={() => setSelectedIdx((i) => Math.max(0, i - 1))}
          onNext={() => setSelectedIdx((i) => Math.min(rows.length - 1, i + 1))}
          hasPrev={selectedIdx > 0}
          hasNext={selectedIdx >= 0 && selectedIdx < rows.length - 1}
        />
      ) : null}
    </>
  );
}
